import { Popover, Transition } from "@headlessui/react";
import {
  IconBell,
  IconCheck,
  IconInbox,
  IconMessageChatbot,
  IconMessageCircle,
  IconSend,
  IconSparkles,
} from "@tabler/icons-react";
import classNames from "classnames";
import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Notification as NotificationType,
  useFetchNotifications,
  useSeenNotification,
} from "@common/features/Notification";
import { __, formatFromNow, formatLocalizedNumber } from "@common/helpers/i18n";
import { ForwardChevronIcon } from "@app/elements/helpers";
import { CounterBadge } from "@app/elements/text";
import { useDocumentDirection } from "@app/hooks";

type NotificationRoute =
  | { type: "internal"; to: string }
  | { type: "external"; href: string };

function getConfiguredAppOrigin() {
  try {
    return import.meta.env.VITE_APP_URL
      ? new URL(import.meta.env.VITE_APP_URL).origin
      : null;
  } catch {
    return null;
  }
}

function resolveNotificationRoute(url: string): NotificationRoute {
  const value = url.trim();

  if (!value) {
    return { type: "internal", to: "/" };
  }

  if (value.startsWith("/")) {
    return { type: "internal", to: value };
  }

  try {
    const parsedUrl = new URL(value);
    const appOrigin = getConfiguredAppOrigin();

    if (
      parsedUrl.origin === window.location.origin ||
      parsedUrl.origin === appOrigin
    ) {
      return {
        type: "internal",
        to: `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`,
      };
    }

    return { type: "external", href: parsedUrl.toString() };
  } catch {
    return { type: "internal", to: `/${value.replace(/^\/+/, "")}` };
  }
}

function getNotificationIcon(notification: NotificationType) {
  const text = `${notification.title} ${notification.content} ${notification.url}`;

  if (text.includes("comment") || text.includes(__("Comment")))
    return IconMessageCircle;
  if (
    text.includes("inbox") ||
    text.includes(__("permissions.automation_message"))
  )
    return IconInbox;
  if (text.includes("automation") || text.includes(__("Answer")))
    return IconMessageChatbot;
  if (text.includes("post") || text.includes(__("Publishing"))) return IconSend;

  return IconSparkles;
}

export default function Notification() {
  const { data, isLoading } = useFetchNotifications();
  const seenNotification = useSeenNotification();
  const direction = useDocumentDirection();
  const [visibleUnread, setVisibleUnread] = useState(0);

  const unread = data
    ? data.filter((notification) => notification.seen === 0).length
    : 0;

  useEffect(() => {
    setVisibleUnread(unread);
  }, [unread]);

  return (
    <Popover className="relative">
      {({ open }) => (
        <>
          <Popover.Button
            className={classNames(
              "group relative inline-flex h-10 w-10 items-center justify-center rounded-md border border-transparent text-foreground transition-all duration-150 hover:border-border hover:bg-muted focus:outline-none focus-visible:outline-none",
              open && "border-border bg-muted text-primary",
              !open && "focus:border-transparent focus:bg-transparent",
            )}
            onClick={() => {
              if (!open) {
                window.setTimeout(() => seenNotification.mutate(), 800);
              }
            }}
          >
            <span className="sr-only">{__("Notifications")}</span>
            <span className="relative inline-flex h-6 w-6 items-center justify-center">
              <IconBell size={21} stroke={2} aria-hidden="true" />
              {visibleUnread !== 0 && (
                <CounterBadge
                  value={visibleUnread}
                  tone="danger"
                  size="xs"
                  className="absolute -end-2 -top-2 z-10 h-4 min-w-4 bg-destructive px-1 text-2xs font-extrabold text-destructive-foreground shadow-sm ring-2 ring-card"
                />
              )}
            </span>
          </Popover.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel
              anchor={{ to: "bottom start", gap: 10, padding: 16 }}
              portal
              className="z-popover w-[min(24rem,calc(100vw-2rem))] text-start"
              dir={direction}
            >
              <div className="overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-medium">
                <div className="flex items-center justify-between gap-4 border-b border-border px-4 py-3.5">
                  <div className="min-w-0 flex-1 text-start">
                    <h2 className="text-sm font-bold leading-6 text-foreground">
                      {__("Notifications")}
                    </h2>
                    <p className="text-xs font-medium leading-5 text-muted-foreground">
                      {visibleUnread !== 0
                        ? __("%{count} new items", {
                            count: formatLocalizedNumber(visibleUnread),
                          })
                        : __("All items have been seen")}
                    </p>
                  </div>
                  <CounterBadge
                    value={
                      visibleUnread !== 0 ? (
                        visibleUnread
                      ) : (
                        <IconCheck size={16} stroke={2} />
                      )
                    }
                    tone={visibleUnread !== 0 ? "primary" : "neutral"}
                    size="lg"
                    className={classNames(
                      "min-w-8",
                      visibleUnread === 0 && "bg-muted",
                    )}
                  />
                </div>

                <div className="max-h-[min(28rem,calc(100vh-8rem))] overflow-y-auto p-2">
                  {isLoading && (
                    <div className="space-y-2 p-2">
                      {[1, 2, 3].map((item) => (
                        <div
                          key={item}
                          className="flex items-start gap-3 rounded-md p-3"
                        >
                          <div className="h-9 w-9 rounded-md bg-muted" />
                          <div className="min-w-0 flex-1 space-y-2">
                            <div className="h-3 w-2/5 rounded bg-muted" />
                            <div className="h-3 w-4/5 rounded bg-muted" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {!isLoading && (!data || data.length === 0) && (
                    <div className="flex flex-col items-center justify-center px-6 py-10 text-center">
                      <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                        <IconBell size={24} stroke={2} />
                      </span>
                      <p className="mt-3 text-sm font-bold text-foreground">
                        {__("You have no new notifications")}
                      </p>
                      <p className="mt-1 text-xs leading-5 text-muted-foreground">
                        {__("Important account items appear here.")}
                      </p>
                    </div>
                  )}

                  {!isLoading &&
                    data?.map((notification) => {
                      const Icon = getNotificationIcon(notification);
                      const isUnread = notification.seen === 0;
                      const route = resolveNotificationRoute(notification.url);
                      const className = classNames(
                        "group flex min-w-0 items-start gap-3 rounded-md p-3 text-start transition-colors hover:bg-muted",
                        isUnread && "bg-primary/5",
                      );
                      const content = (
                        <>
                          <span
                            className={classNames(
                              "mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md",
                              isUnread
                                ? "bg-primary/10 text-primary"
                                : "bg-muted text-muted-foreground",
                            )}
                          >
                            <Icon size={19} stroke={2} />
                          </span>
                          <span className="min-w-0 flex-1 text-start">
                            <span className="flex min-w-0 items-start gap-2">
                              <span className="min-w-0 flex-1 truncate text-sm font-bold leading-6 text-foreground">
                                {notification.title}
                              </span>
                              {isUnread && (
                                <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-primary" />
                              )}
                            </span>
                            <span className="mt-0.5 block line-clamp-2 text-xs font-medium leading-5 text-muted-foreground">
                              {notification.content}
                            </span>
                            <span className="mt-2 block text-xs font-medium text-muted-foreground">
                              {formatFromNow(notification.created_at)}
                            </span>
                          </span>
                          <span className="mt-2 flex h-5 w-5 flex-shrink-0 items-center justify-center text-muted-foreground">
                            <ForwardChevronIcon
                              size={16}
                              stroke={2}
                              className="transition-transform group-hover:-translate-x-0.5"
                            />
                          </span>
                        </>
                      );

                      return route.type === "external" ? (
                        <a
                          className={className}
                          href={route.href}
                          key={notification.id}
                          rel="noopener noreferrer"
                        >
                          {content}
                        </a>
                      ) : (
                        <Link
                          className={className}
                          key={notification.id}
                          to={route.to}
                        >
                          {content}
                        </Link>
                      );
                    })}
                </div>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}
